import type { GridPaymentGridData } from '../../../types/paymentTypes';
import { parseTermId } from '../../../utils/termIdParser';
import { shouldSkipTermOverrideForColumn } from '../../../utils/paymentGridSourceUtils';
import type { Offer, OfferType } from '@makemydeal/dr-platform-types';
import { RESIDUAL_DOLLAR, RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import type { PartialPenciledInputsWithAllTypes, PenciledInputsState } from '../../../types/commonTypes';
import * as types from '../../../types/actionTypes';
import { updateInputWithValue, updateOfferTypeWithInputs } from '../helpers';

/** Records the term a UPQ deal-screen residual was typed against, so a term switch repointing the deal-level slots does not discard it. */
const withTypedTermResidual = (
    state: PenciledInputsState,
    offerType: OfferType,
    months: number | undefined,
    isPaymentGridRedesignEnabled: boolean,
    residualInfo: Record<string, unknown>
) => {
    if (!isPaymentGridRedesignEnabled || months == null) return undefined;

    const prevOverridesByTerm = state.byOfferType?.[offerType]?.overridesByTerm;

    return {
        overridesByTerm: {
            ...prevOverridesByTerm,
            [months]: {
                ...prevOverridesByTerm?.[months],
                residualInfo: { ...prevOverridesByTerm?.[months]?.residualInfo, ...residualInfo }
            }
        }
    };
};

export const handleResidualCases = (
    state: PenciledInputsState,
    action: any,
    offerType: OfferType,
    offer: Offer,
    isUserProgramQuotes: boolean,
    isPaymentGridRedesignEnabled = false
): PartialPenciledInputsWithAllTypes => {
    switch (action.type) {
        case types.UPDATED_ADJUSTED_RESIDUAL_DOLLAR_OVERRIDE: {
            const { months: dollarMonths, value: dollarValue } = action.payload;
            if (!state.byOfferType) return state;
            if (isUserProgramQuotes) {
                const newState = {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [offerType]: {
                            ...state.byOfferType?.[offerType],
                            adjustedResidualAmountOverride: dollarValue,
                            adjustedResidualTypeOverride: RESIDUAL_DOLLAR,
                            ...withTypedTermResidual(state, offerType, dollarMonths, isPaymentGridRedesignEnabled, {
                                adjustedResidualAmount: dollarValue,
                                adjustedResidualType: RESIDUAL_DOLLAR
                            })
                        }
                    }
                };
                return newState;
            }

            const prevOverridesByTerm = state.byOfferType?.[offerType]?.overridesByTerm || {};
            const newOverridesByTerm = {
                ...prevOverridesByTerm,
                [dollarMonths]: {
                    ...prevOverridesByTerm?.[dollarMonths],
                    residualInfo: {
                        ...prevOverridesByTerm?.[dollarMonths]?.residualInfo,
                        adjustedResidualAmount: dollarValue,
                        adjustedResidualType: RESIDUAL_DOLLAR
                    }
                }
            };
            const residualDollarTermUpdates = updateInputWithValue(
                state.byOfferType?.[offerType],
                'overridesByTerm',
                newOverridesByTerm
            );
            const leaseUpdatesForAdjResidualDollar = updateOfferTypeWithInputs(
                state.byOfferType,
                residualDollarTermUpdates,
                offerType
            );

            return {
                ...state,
                byOfferType: leaseUpdatesForAdjResidualDollar
            };
        }

        case types.UPDATED_ADJUSTED_RESIDUAL_PERCENT_OVERRIDE: {
            const { months: percentMonths, value: percentValue } = action.payload;
            if (!state.byOfferType) return state;
            if (isUserProgramQuotes) {
                const newState = {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [offerType]: {
                            ...state.byOfferType?.[offerType],
                            adjustedResidualPercentOverride: percentValue,
                            adjustedResidualTypeOverride: RESIDUAL_PERCENT,
                            ...withTypedTermResidual(state, offerType, percentMonths, isPaymentGridRedesignEnabled, {
                                adjustedResidualPercent: percentValue,
                                adjustedResidualType: RESIDUAL_PERCENT
                            })
                        }
                    }
                };
                return newState;
            }

            const prevOverridesByTerm = state.byOfferType?.[offerType]?.overridesByTerm || {};
            const newOverridesByTerm = {
                ...prevOverridesByTerm,
                [percentMonths]: {
                    ...prevOverridesByTerm?.[percentMonths],
                    residualInfo: {
                        ...prevOverridesByTerm?.[percentMonths]?.residualInfo,
                        adjustedResidualPercent: percentValue,
                        adjustedResidualType: RESIDUAL_PERCENT
                    }
                }
            };
            const residualPercentTermUpdates = updateInputWithValue(
                state.byOfferType?.[offerType],
                'overridesByTerm',
                newOverridesByTerm
            );
            const adjustedResidualPercentOverride = updateOfferTypeWithInputs(
                state.byOfferType,
                residualPercentTermUpdates,
                offerType
            );

            return {
                ...state,
                byOfferType: adjustedResidualPercentOverride
            };
        }

        case types.UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE: {
            const { months: dollarMonths, value: dollarValue } = action.payload;
            if (!state.byOfferType) return state;
            if (isUserProgramQuotes) {
                const newState = {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [offerType]: {
                            ...state.byOfferType?.[offerType],
                            baseResidualAmountOverride: dollarValue,
                            programResidualOverride: dollarValue,
                            baseResidualTypeOverride: RESIDUAL_DOLLAR,
                            ...withTypedTermResidual(state, offerType, dollarMonths, isPaymentGridRedesignEnabled, {
                                baseResidualAmount: dollarValue,
                                programResidual: dollarValue,
                                baseResidualType: RESIDUAL_DOLLAR
                            })
                        }
                    }
                };
                return newState;
            }

            const prevOverridesByTerm = state.byOfferType?.[offerType]?.overridesByTerm || {};
            const newOverridesByTerm = {
                ...prevOverridesByTerm,
                [dollarMonths]: {
                    ...prevOverridesByTerm?.[dollarMonths],
                    residualInfo: {
                        ...prevOverridesByTerm?.[dollarMonths]?.residualInfo,
                        baseResidualAmount: dollarValue,
                        programResidual: dollarValue,
                        baseResidualType: RESIDUAL_DOLLAR
                    }
                }
            };
            const baseResidualDollarTermUpdates = updateInputWithValue(
                state.byOfferType?.[offerType],
                'overridesByTerm',
                newOverridesByTerm
            );
            const leaseUpdatesForBaseResidualDollar = updateOfferTypeWithInputs(
                state.byOfferType,
                baseResidualDollarTermUpdates,
                offerType
            );

            return {
                ...state,
                byOfferType: leaseUpdatesForBaseResidualDollar
            };
        }

        case types.UPDATED_BASE_RESIDUAL_PERCENT_OVERRIDE: {
            const { months: percentMonths, value: percentValue } = action.payload;
            if (!state.byOfferType) return state;
            if (isUserProgramQuotes) {
                const newState = {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [offerType]: {
                            ...state.byOfferType?.[offerType],
                            baseResidualPercentOverride: percentValue,
                            programResidualPercentOverride: percentValue,
                            baseResidualTypeOverride: RESIDUAL_PERCENT,
                            ...withTypedTermResidual(state, offerType, percentMonths, isPaymentGridRedesignEnabled, {
                                baseResidualPercent: percentValue,
                                programResidualPercent: percentValue,
                                baseResidualType: RESIDUAL_PERCENT
                            })
                        }
                    }
                };
                return newState;
            }

            const prevOverridesByTerm = state.byOfferType?.[offerType]?.overridesByTerm || {};
            const newOverridesByTerm = {
                ...prevOverridesByTerm,
                [percentMonths]: {
                    ...prevOverridesByTerm?.[percentMonths],
                    residualInfo: {
                        ...prevOverridesByTerm?.[percentMonths]?.residualInfo,
                        baseResidualPercent: percentValue,
                        programResidualPercent: percentValue,
                        baseResidualType: RESIDUAL_PERCENT
                    }
                }
            };
            const baseResidualPercentTermUpdates = updateInputWithValue(
                state.byOfferType?.[offerType],
                'overridesByTerm',
                newOverridesByTerm
            );
            const baseResidualPercentOverride = updateOfferTypeWithInputs(
                state.byOfferType,
                baseResidualPercentTermUpdates,
                offerType
            );

            return {
                ...state,
                byOfferType: baseResidualPercentOverride
            };
        }

        case types.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE: {
            const {
                months: dollarMonths,
                value: dollarValue,
                isSelectedTerm = true,
                syncBaseResidual = false,
                clearBaseResidual = false
            } = action.payload;
            const { middleware, selectedOfferType } = action.meta || {};
            if (!state.byOfferType) return state;

            // The payment payload prices a lease off `baseResidualValue`, read from the base residual rather
            // than the program residual, so an edit meant to reprice has to write both — the pair
            // UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE writes. See `syncBaseResidual` on the action creator.
            const syncBaseResidualDollar = isPaymentGridRedesignEnabled && syncBaseResidual;
            const clearBaseResidualDollar = isPaymentGridRedesignEnabled && clearBaseResidual && !syncBaseResidualDollar;

            // Legacy UPQ behaviour: residual is a single deal-level value with no per-term home, so the
            // payment grid cannot hold one residual per term column. Retained while the toggle is off.
            if (isUserProgramQuotes && !isPaymentGridRedesignEnabled) {
                // A caller that names a term other than the selected one is not describing the deal's
                // residual, so it must not write the deal-level value. Restoring a saved grid dispatches once
                // per column, and without this the last column processed would win. Defaults to true, so
                // every caller that predates the payload flag behaves exactly as before.
                if (!isSelectedTerm) return state;

                const newState = {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [offerType]: {
                            ...state.byOfferType?.[offerType],
                            programResidualOverride: dollarValue,
                            baseResidualTypeOverride: RESIDUAL_DOLLAR
                        }
                    }
                };
                return newState;
            }

            const offerTypeForDollarUpdate: OfferType = selectedOfferType || offerType;
            const prevOverridesByTerm = state.byOfferType?.[offerTypeForDollarUpdate]?.overridesByTerm || {};
            const newOverridesByTerm = {
                ...prevOverridesByTerm,
                [dollarMonths]: {
                    ...prevOverridesByTerm?.[dollarMonths],
                    residualInfo: {
                        ...prevOverridesByTerm?.[dollarMonths]?.residualInfo,
                        programResidual: dollarValue,
                        ...(syncBaseResidualDollar && { baseResidualAmount: dollarValue }),
                        ...(clearBaseResidualDollar && {
                            baseResidualAmount: undefined,
                            baseResidualPercent: undefined
                        }),
                        // The type moves only together with its value, the same rule the deal-level write below
                        // follows: stamping it alone leaves `applyPerTermResidualOverrides` resolving this term
                        // from the other unit's field, which still holds a stale value. Written unconditionally
                        // while the toggle is off, so consumers on the current behaviour are unaffected.
                        ...((!isPaymentGridRedesignEnabled || syncBaseResidualDollar) && {
                            baseResidualType: RESIDUAL_DOLLAR
                        })
                    }
                }
            };
            const skipTermResidual =
                isPaymentGridRedesignEnabled &&
                shouldSkipTermOverrideForColumn(
                    (
                        state.byOfferType?.[offerTypeForDollarUpdate as keyof typeof state.byOfferType]
                            ?.paymentGridData as GridPaymentGridData
                    )?.terms,
                    (
                        state.byOfferType?.[offerTypeForDollarUpdate as keyof typeof state.byOfferType]
                            ?.paymentGridData as GridPaymentGridData
                    )?.activeCell?.columnIndex,
                    dollarMonths,
                    action.meta?.gridColumnIndex,
                    parseTermId(state.byOfferType?.[offerTypeForDollarUpdate as keyof typeof state.byOfferType]?.term)?.termIdParams
                        ?.term
                );

            let programResidualDollarTermUpdates = updateInputWithValue(
                state.byOfferType?.[offerTypeForDollarUpdate],
                'overridesByTerm',
                skipTermResidual ? prevOverridesByTerm : newOverridesByTerm
            );

            // Under UPQ the selected term also drives the deal-level override that the UPQ payment payload
            // reads, so keep it in sync the way the sell rate handler does.
            if (isUserProgramQuotes && isSelectedTerm && !skipTermResidual) {
                programResidualDollarTermUpdates = {
                    ...programResidualDollarTermUpdates,
                    programResidualOverride: dollarValue,
                    // Only claim the deal's residual type when this edit also sets the base residual.
                    // Flipping the type on its own would leave the payload reading baseResidualValue from
                    // the other unit's field, which still holds a stale value.
                    ...(syncBaseResidualDollar && {
                        baseResidualAmountOverride: dollarValue,
                        baseResidualTypeOverride: RESIDUAL_DOLLAR
                    }),
                    ...(clearBaseResidualDollar && {
                        baseResidualAmountOverride: undefined,
                        baseResidualPercentOverride: undefined
                    })
                };
            }

            const leaseUpdatesForProgramResidualDollar = updateOfferTypeWithInputs(
                state.byOfferType,
                programResidualDollarTermUpdates,
                offerTypeForDollarUpdate
            );

            const updatedOfferTypeForDollar = {
                ...state.byOfferType?.[offerTypeForDollarUpdate],
                ...leaseUpdatesForProgramResidualDollar[
                    offerTypeForDollarUpdate as keyof typeof leaseUpdatesForProgramResidualDollar
                ],
                ...(middleware?.payment && {
                    paymentGridData: {
                        ...state.byOfferType?.[offerTypeForDollarUpdate]?.paymentGridData,
                        lastUpdatedInput: `input_residual_${dollarMonths}`
                    }
                })
            };

            return {
                ...state,
                byOfferType: { ...state.byOfferType, [offerTypeForDollarUpdate]: updatedOfferTypeForDollar }
            };
        }

        case types.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE: {
            const {
                months: percentMonths,
                value: percentValue,
                isSelectedTerm = true,
                syncBaseResidual = false,
                clearBaseResidual = false
            } = action.payload;
            const { middleware, selectedOfferType } = action.meta || {};
            if (!state.byOfferType) return state;

            // See the dollar case above: without the base residual the grid's typed value reaches PS as
            // `programResidualValue` only, PS reprices from the stale base residual, and neither the payment
            // nor the deal screen's Base Residual field moves.
            const syncBaseResidualPercent = isPaymentGridRedesignEnabled && syncBaseResidual;
            const clearBaseResidualPercent = isPaymentGridRedesignEnabled && clearBaseResidual && !syncBaseResidualPercent;

            // Legacy UPQ behaviour: residual is a single deal-level value with no per-term home, so the
            // payment grid cannot hold one residual per term column. Retained while the toggle is off.
            if (isUserProgramQuotes && !isPaymentGridRedesignEnabled) {
                // See the dollar case: a non-selected term must not claim the deal-level residual, or a
                // multi-column restore ends up keeping whichever column was processed last.
                if (!isSelectedTerm) return state;

                const newState = {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [offerType]: {
                            ...state.byOfferType?.[offerType],
                            programResidualPercentOverride: percentValue,
                            baseResidualTypeOverride: RESIDUAL_PERCENT
                        }
                    }
                };
                return newState;
            }

            const offerTypeForPercentUpdate: OfferType = selectedOfferType || offerType;
            const prevOverridesByTerm = state.byOfferType?.[offerTypeForPercentUpdate]?.overridesByTerm || {};
            const newOverridesByTerm = {
                ...prevOverridesByTerm,
                [percentMonths]: {
                    ...prevOverridesByTerm?.[percentMonths],
                    residualInfo: {
                        ...prevOverridesByTerm?.[percentMonths]?.residualInfo,
                        programResidualPercent: percentValue,
                        ...(syncBaseResidualPercent && { baseResidualPercent: percentValue }),
                        ...(clearBaseResidualPercent && {
                            baseResidualAmount: undefined,
                            baseResidualPercent: undefined
                        }),
                        // See the dollar case above: the type moves only together with the base residual value.
                        ...((!isPaymentGridRedesignEnabled || syncBaseResidualPercent) && {
                            baseResidualType: RESIDUAL_PERCENT
                        })
                    }
                }
            };
            const skipTermResidual =
                isPaymentGridRedesignEnabled &&
                shouldSkipTermOverrideForColumn(
                    (
                        state.byOfferType?.[offerTypeForPercentUpdate as keyof typeof state.byOfferType]
                            ?.paymentGridData as GridPaymentGridData
                    )?.terms,
                    (
                        state.byOfferType?.[offerTypeForPercentUpdate as keyof typeof state.byOfferType]
                            ?.paymentGridData as GridPaymentGridData
                    )?.activeCell?.columnIndex,
                    percentMonths,
                    action.meta?.gridColumnIndex,
                    parseTermId(state.byOfferType?.[offerTypeForPercentUpdate as keyof typeof state.byOfferType]?.term)
                        ?.termIdParams?.term
                );

            let programResidualPercentTermUpdates = updateInputWithValue(
                state.byOfferType?.[offerTypeForPercentUpdate],
                'overridesByTerm',
                skipTermResidual ? prevOverridesByTerm : newOverridesByTerm
            );

            // Under UPQ the selected term also drives the deal-level override that the UPQ payment payload
            // reads, so keep it in sync the way the sell rate handler does.
            if (isUserProgramQuotes && isSelectedTerm && !skipTermResidual) {
                programResidualPercentTermUpdates = {
                    ...programResidualPercentTermUpdates,
                    programResidualPercentOverride: percentValue,
                    // See the dollar case above: the type moves only together with the base residual value.
                    ...(syncBaseResidualPercent && {
                        baseResidualPercentOverride: percentValue,
                        baseResidualTypeOverride: RESIDUAL_PERCENT
                    }),
                    ...(clearBaseResidualPercent && {
                        baseResidualAmountOverride: undefined,
                        baseResidualPercentOverride: undefined
                    })
                };
            }

            const programResidualPercentOverride = updateOfferTypeWithInputs(
                state.byOfferType,
                programResidualPercentTermUpdates,
                offerTypeForPercentUpdate
            );

            const updatedOfferTypeForPercent = {
                ...state.byOfferType?.[offerTypeForPercentUpdate],
                ...programResidualPercentOverride[offerTypeForPercentUpdate as keyof typeof programResidualPercentOverride],
                ...(middleware?.payment && {
                    paymentGridData: {
                        ...state.byOfferType?.[offerTypeForPercentUpdate]?.paymentGridData,
                        lastUpdatedInput: `input_residual_${percentMonths}`
                    }
                })
            };

            return {
                ...state,
                byOfferType: { ...state.byOfferType, [offerTypeForPercentUpdate]: updatedOfferTypeForPercent }
            };
        }

        case types.REMOVED_PROGRAM_RESIDUAL_OVERRIDE: {
            const { months, isSelectedTerm = true } = action.payload;
            const { middleware, selectedOfferType } = action.meta || {};
            if (!state.byOfferType) return state;

            // Per-column removal only means something where the residual IS per column. While the toggle is off
            // the residual is one deal-level value, so there is no single column to take back and consumers on
            // today's behaviour keep it.
            if (!isPaymentGridRedesignEnabled) return state;

            const offerTypeForRemoval: OfferType = selectedOfferType || offerType;
            const prevOverridesByTerm: Record<number, any> = state.byOfferType?.[offerTypeForRemoval]?.overridesByTerm || {};
            const prevTermOverrides = prevOverridesByTerm[months];
            if (!prevTermOverrides?.residualInfo) return state;

            // Only the keys a grid residual edit writes. `residualInfo` has other writers - a reopened deal
            // hydrates `adjustedResidual*` onto the same record - and dropping the record whole would discard
            // values the user never touched.
            const {
                programResidual,
                programResidualPercent,
                baseResidualAmount,
                baseResidualPercent,
                baseResidualType: _removedType,
                ...keptResidualInfo
            } = prevTermOverrides.residualInfo;

            // Collapse what is left the way `dropPinnedOverridesForTerm` does: an empty `residualInfo`, or an
            // entry holding nothing but one, would read as a column the user has priced.
            const { residualInfo: _removedResidualInfo, ...termOverridesWithoutResidual } = prevTermOverrides;
            const keptTermOverrides = Object.keys(keptResidualInfo).length
                ? { ...termOverridesWithoutResidual, residualInfo: keptResidualInfo }
                : termOverridesWithoutResidual;

            const { [months]: _removedTerm, ...remainingOverridesByTerm } = prevOverridesByTerm;
            const newOverridesByTerm = Object.keys(keptTermOverrides).length
                ? { ...prevOverridesByTerm, [months]: keptTermOverrides }
                : remainingOverridesByTerm;

            const skipTermResidual =
                isPaymentGridRedesignEnabled &&
                shouldSkipTermOverrideForColumn(
                    (
                        state.byOfferType?.[offerTypeForRemoval as keyof typeof state.byOfferType]
                            ?.paymentGridData as GridPaymentGridData
                    )?.terms,
                    (
                        state.byOfferType?.[offerTypeForRemoval as keyof typeof state.byOfferType]
                            ?.paymentGridData as GridPaymentGridData
                    )?.activeCell?.columnIndex,
                    months,
                    action.meta?.gridColumnIndex,
                    parseTermId(state.byOfferType?.[offerTypeForRemoval as keyof typeof state.byOfferType]?.term)?.termIdParams
                        ?.term
                );

            let removalTermUpdates = updateInputWithValue(
                state.byOfferType?.[offerTypeForRemoval],
                'overridesByTerm',
                skipTermResidual ? prevOverridesByTerm : newOverridesByTerm
            );

            // The grid edit wrote the deal level from this column's own value (`syncBaseResidual`), and for the
            // selected term that is what the cell repopulates from and what the UPQ payload prices off - so
            // leaving it would take the column's residual back everywhere except where it is doing the pricing.
            //
            // Each key goes only while it still holds the value being removed: a later deal-screen edit owns it,
            // and there is no snapshot of what it held before the grid wrote over it. `baseResidualTypeOverride`
            // stays either way - it names a unit rather than a user's figure, and a deal with no residual
            // override still carries one.
            if (isUserProgramQuotes && isSelectedTerm && !skipTermResidual) {
                const dealInputs = state.byOfferType?.[offerTypeForRemoval];

                removalTermUpdates = {
                    ...removalTermUpdates,
                    ...(programResidual != null &&
                        dealInputs?.programResidualOverride === programResidual && { programResidualOverride: undefined }),
                    ...(baseResidualAmount != null &&
                        dealInputs?.baseResidualAmountOverride === baseResidualAmount && {
                            baseResidualAmountOverride: undefined
                        }),
                    ...(programResidualPercent != null &&
                        dealInputs?.programResidualPercentOverride === programResidualPercent && {
                            programResidualPercentOverride: undefined
                        }),
                    ...(baseResidualPercent != null &&
                        dealInputs?.baseResidualPercentOverride === baseResidualPercent && {
                            baseResidualPercentOverride: undefined
                        })
                };
            }

            const leaseUpdatesForRemoval = updateOfferTypeWithInputs(state.byOfferType, removalTermUpdates, offerTypeForRemoval);

            const updatedOfferTypeForRemoval = {
                ...state.byOfferType?.[offerTypeForRemoval],
                ...leaseUpdatesForRemoval[offerTypeForRemoval as keyof typeof leaseUpdatesForRemoval],
                ...(middleware?.payment && {
                    paymentGridData: {
                        ...state.byOfferType?.[offerTypeForRemoval]?.paymentGridData,
                        lastUpdatedInput: `input_residual_${months}`
                    }
                })
            };

            return {
                ...state,
                byOfferType: { ...state.byOfferType, [offerTypeForRemoval]: updatedOfferTypeForRemoval }
            };
        }

        case types.UPDATED_RESIDUAL_FOR_SELECTED_TERM_UPQ: {
            const { selectedOfferType } = action.meta || {};
            if (!state.byOfferType) return state;

            // Deal-level slots only: writing a per-term one would persist an override the user never made.
            if (!isPaymentGridRedesignEnabled || !isUserProgramQuotes) return state;

            const offerTypeForResync: OfferType = selectedOfferType || offerType;
            const nodeForResync = state.byOfferType?.[offerTypeForResync];
            if (!nodeForResync) return state;

            const {
                unitType,
                adjustedUnitType,
                baseResidualAmount,
                baseResidualPercent,
                programResidual,
                programResidualPercent,
                adjustedResidualAmount,
                adjustedResidualPercent
            } = action.payload;

            // Every figure the payload carries, never a blank: blanking a slot fails `hasRequiredFieldsForUPQ`.
            const residualUpdate = {
                ...(baseResidualAmount != null && { baseResidualAmountOverride: baseResidualAmount }),
                ...(baseResidualPercent != null && { baseResidualPercentOverride: baseResidualPercent }),
                ...(programResidual != null && { programResidualOverride: programResidual }),
                ...(programResidualPercent != null && { programResidualPercentOverride: programResidualPercent }),
                ...(adjustedResidualAmount != null && { adjustedResidualAmountOverride: adjustedResidualAmount }),
                ...(adjustedResidualPercent != null && { adjustedResidualPercentOverride: adjustedResidualPercent }),
                baseResidualTypeOverride: unitType,
                ...(adjustedUnitType != null && { adjustedResidualTypeOverride: adjustedUnitType })
            };

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerTypeForResync]: { ...nodeForResync, ...residualUpdate }
                }
            };
        }

        case types.UPDATE_RESIDUAL_TAB_DISPLAY_TYPE: {
            const residualType = action.payload;
            if (!state.byOfferType) return state;
            if (isUserProgramQuotes) {
                return {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [offerType]: {
                            ...state.byOfferType?.[offerType],
                            baseResidualTypeOverride: residualType
                        }
                    }
                };
            }

            const selectedTermId = state.byOfferType?.[offerType]?.term;
            const months = offer?.terms?.byId?.[selectedTermId!]?.months;
            if (months == null) return state;

            const prevOverridesByTerm = state.byOfferType?.[offerType]?.overridesByTerm || {};
            const newOverridesByTerm = {
                ...prevOverridesByTerm,
                [months]: {
                    ...prevOverridesByTerm?.[months],
                    residualInfo: {
                        ...prevOverridesByTerm?.[months]?.residualInfo,
                        baseResidualType: residualType
                    }
                }
            };
            const displayTypeTermUpdates = updateInputWithValue(
                state.byOfferType?.[offerType],
                'overridesByTerm',
                newOverridesByTerm
            );
            const leaseUpdatesForDisplayType = updateOfferTypeWithInputs(state.byOfferType, displayTypeTermUpdates, offerType);

            return {
                ...state,
                byOfferType: leaseUpdatesForDisplayType
            };
        }

        default: {
            return state;
        }
    }
};
